import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  LogOut, Search, Mail, Phone, Target, Trash2, Lock, ArrowLeft, Inbox, User,
} from "lucide-react";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "onevo_admin_token";

const authHeader = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem(TOKEN_KEY)}` },
});

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("it-IT", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username, password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      onLogin();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Credenziali errate.");
    } finally {
      setLoading(false);
    }
  };

  const field =
    "bg-transparent border border-white/15 rounded-[3px] h-12 text-white placeholder:text-white/35 focus-visible:ring-0 focus-visible:border-white";

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 font-mono-label text-[10px] text-white/45 hover:text-white transition-colors mb-10">
          <ArrowLeft size={14} /> Torna al sito
        </button>
        <div className="flex items-center gap-3 mb-2">
          <Lock size={20} className="text-white" />
          <h1 className="font-display text-3xl text-white">Area Riservata</h1>
        </div>
        <p className="text-white/50 text-sm mb-8">Accedi alla dashboard delle richieste di contatto.</p>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div>
            <label className="font-mono-label text-[10px] text-white/45">Username</label>
            <Input value={username} onChange={(e) => { setUsername(e.target.value); setErr(""); }} placeholder="Username" className={`${field} mt-1.5`} />
          </div>
          <div>
            <label className="font-mono-label text-[10px] text-white/45">Password</label>
            <Input type="password" value={password} onChange={(e) => { setPassword(e.target.value); setErr(""); }} placeholder="Password" className={`${field} mt-1.5`} />
          </div>
          {err && <span className="text-red-400 text-sm">{err}</span>}
          <button type="submit" disabled={loading} className="bg-white text-black font-mono-label text-[11px] py-4 rounded-[3px] hover:bg-white/85 transition-colors disabled:opacity-60 mt-2">
            {loading ? "Accesso…" : "Accedi"}
          </button>
        </form>
      </div>
    </div>
  );
};

const ContactCard = ({ contact, onDelete }) => (
  <div className="border border-white/10 rounded-sm p-6 hover:border-white/25 transition-colors">
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
          <User size={18} className="text-white" />
        </div>
        <div>
          <div className="text-white font-medium text-lg">{contact.name}</div>
          <div className="font-mono-label text-[9px] text-white/40 mt-0.5">{fmtDate(contact.created_at)}</div>
        </div>
      </div>
      {contact.goal && (
        <span className="flex items-center gap-1.5 font-mono-label text-[9px] text-white/70 border border-white/15 px-3 py-1.5 rounded-full">
          <Target size={11} /> {contact.goal}
        </span>
      )}
    </div>

    <div className="flex flex-col gap-2 mt-5">
      <a href={`mailto:${contact.email}`} className="flex items-center gap-2.5 text-white/75 hover:text-white transition-colors text-sm">
        <Mail size={15} className="text-white/40" /> {contact.email}
      </a>
      <a href={`tel:${contact.phone}`} className="flex items-center gap-2.5 text-white/75 hover:text-white transition-colors text-sm">
        <Phone size={15} className="text-white/40" /> {contact.phone}
      </a>
    </div>

    {contact.message && (
      <p className="text-white/55 text-sm leading-relaxed mt-4 border-l-2 border-white/15 pl-4">{contact.message}</p>
    )}

    <div className="flex justify-end mt-5 pt-4 border-t border-white/10">
      <button onClick={() => onDelete(contact.id)} className="flex items-center gap-2 font-mono-label text-[10px] text-white/45 hover:text-red-400 transition-colors">
        <Trash2 size={13} /> Elimina
      </button>
    </div>
  </div>
);

const ContactsHeader = ({ navigate, onLogout }) => (
  <header className="border-b border-white/10 sticky top-0 bg-[#0a0a0a]/90 backdrop-blur z-10">
    <div className="max-w-[1300px] mx-auto px-6 md:px-8 h-20 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <span className="font-display text-2xl text-white">ONEVO</span>
        <span className="font-mono-label text-[10px] text-white/40 hidden sm:inline">Dashboard Richieste</span>
      </div>
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/")} className="font-mono-label text-[10px] text-white/50 hover:text-white transition-colors px-3">Vai al sito</button>
        <button onClick={onLogout} className="flex items-center gap-2 font-mono-label text-[10px] text-white border border-white/20 px-4 py-2.5 rounded-[3px] hover:bg-white/5 transition-colors">
          <LogOut size={14} /> Esci
        </button>
      </div>
    </div>
  </header>
);

const AdminPage = () => {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(!!sessionStorage.getItem(TOKEN_KEY));
  const [contacts, setContacts] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/contacts`, authHeader());
      setContacts(data);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    document.title = "ONEVO · Dashboard Admin";
    if (authed) load();
  }, [authed, load]);

  const filtered = useMemo(() => {
    if (!query) return contacts;
    const q = query.toLowerCase();
    return contacts.filter(
      (c) => c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.phone?.includes(q)
    );
  }, [contacts, query]);

  const removeOne = useCallback(async (id) => {
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, authHeader());
      setContacts((list) => list.filter((c) => c.id !== id));
    } catch (e) {
      if (e?.response?.status === 401) logout();
    }
  }, [logout]);

  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <ContactsHeader navigate={navigate} onLogout={logout} />

      <main className="max-w-[1300px] mx-auto px-6 md:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <h1 className="font-display text-4xl md:text-5xl text-white">Richieste di contatto</h1>
            <p className="text-white/50 mt-2">{contacts.length} richieste totali</p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca nome, email, telefono…"
              className="bg-white/[0.03] border border-white/15 rounded-[3px] h-11 pl-10 text-white placeholder:text-white/35 focus-visible:ring-0 focus-visible:border-white"
            />
          </div>
        </div>

        {loading ? (
          <div className="border border-white/10 rounded-sm py-24 text-center text-white/40">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="border border-white/10 rounded-sm py-24 flex flex-col items-center text-center">
            <Inbox size={40} className="text-white/25" />
            <p className="text-white/60 mt-4 font-display text-2xl">Nessuna richiesta</p>
            <p className="text-white/40 mt-2 max-w-sm">Le richieste inviate dal form di contatto del sito compariranno qui.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-4">
            {filtered.map((c) => (
              <ContactCard key={c.id} contact={c} onDelete={removeOne} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
